import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/db8df0rk13ra8mig8ppg';

const POSITIONING = [
  { icon: 'ri-compass-3-line', title: '内容聚合', desc: '整合最新电影与电视剧资源，覆盖动作、喜剧、爱情、科幻、悬疑等多种类型。' },
  { icon: 'ri-flashlight-line', title: '快速查找', desc: '支持关键词搜索与多维筛选，帮助追剧、观影用户第一时间找到想看的内容。' },
  { icon: 'ri-shield-star-line', title: '清晰体验', desc: '专业门户布局与舒适的浏览体验，让挑选影视作品变得简单高效。' },
];

const CONTACTS = [
  { icon: 'ri-mail-line', label: '客服邮箱', value: 'support@pengyoumama-online.com' },
  { icon: 'ri-customer-service-2-line', label: '在线时间', value: '工作日 9:00 - 18:00' },
  { icon: 'ri-map-pin-line', label: '合作与版权', value: '内容合作、版权投诉欢迎来函' },
];

export default function AboutPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 朋友的妈妈在线';
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get('website_alt') ?? '').trim();
    if (guard) {
      setStatus('success');
      setFeedback('感谢你的反馈，我们会尽快处理。');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isOk = response.ok && parsed?.code === 'OK';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (isOk && !isSpam) {
        setStatus('success');
        setFeedback('提交成功，感谢你的反馈，我们会尽快回复。');
        form.reset();
      } else {
        setStatus('error');
        setFormError(isSpam ? '提交未通过校验，请稍后重试。' : (String(serverMsg) || '提交失败，请稍后重试。'));
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="w-full">
      <div className="w-full bg-primary-950 text-background-50">
        <div className="px-4 md:px-8 py-10 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-background-300 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-100">关于我们</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl">关于「朋友的妈妈在线」</h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-background-200">
            朋友的妈妈在线是一个专注于最新电影与电视剧资源聚合的观影导航平台。我们面向喜爱追剧、观影的广大网民，
            致力于用丰富的内容覆盖与便捷的搜索观看体验，帮助每一位用户快速发现值得一看的高清影视作品。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {POSITIONING.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 mb-4">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h2 className="font-heading font-semibold text-foreground-950">{item.title}</h2>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          <div>
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-5">联系我们</h2>
            <div className="space-y-4">
              {CONTACTS.map((c) => (
                <div key={c.label} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                    <i className={`${c.icon} text-lg`}></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{c.label}</p>
                    <p className="text-sm text-foreground-900 mt-0.5">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-lg bg-accent-50 border border-accent-200 p-5">
              <h3 className="font-heading font-semibold text-accent-900 flex items-center gap-2">
                <i className="ri-scales-3-line"></i> 版权与免责声明
              </h3>
              <p className="mt-2 text-sm text-accent-900/80 leading-relaxed">
                本站为影视信息浏览与推荐平台，不含播放、下载、会员与支付功能，所有影视资料均来自公开渠道整理，仅用于信息展示。
                如相关权利方对内容有异议，欢迎通过上方联系方式与我们取得联系，我们将及时处理。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-1">意见反馈</h2>
            <p className="text-sm text-foreground-500 mb-5">遇到问题或有想看的内容？欢迎告诉我们。</p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">你的称呼</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入称呼"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">电子邮箱</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收我们的回复"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                />
              </div>

              <div>
                <label htmlFor="topic" className="block text-sm text-foreground-700 mb-1.5">反馈类型</label>
                <select
                  id="topic"
                  name="topic"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="资源缺失">资源缺失</option>
                  <option value="使用问题">使用问题</option>
                  <option value="合作洽谈">合作洽谈</option>
                  <option value="版权投诉">版权投诉</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">反馈内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请描述你的建议或问题（500字以内）"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400 resize-none"
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                className="contact-guard-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> 提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i> 提交反馈
                  </>
                )}
              </button>

              {status === 'success' && (
                <p className="flex items-center gap-2 text-sm text-secondary-700 bg-secondary-50 border border-secondary-200 rounded-md px-3.5 py-2.5">
                  <i className="ri-checkbox-circle-line"></i> {feedback}
                </p>
              )}
              {status === 'error' && (
                <p className="flex items-center gap-2 text-sm text-accent-900 bg-accent-50 border border-accent-200 rounded-md px-3.5 py-2.5">
                  <i className="ri-error-warning-line"></i> {formError}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}